import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Command, AlertTriangle, TestTube2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import apiClient from '@/api/client';
import { useUserModules, useCurrentUserModule } from '@/hooks/useUserModule';
import { isModuleOverride } from '@/hooks/useOverrideStatus';
import type { UserModule } from '@/hooks/useUserModule';
import { cn } from '@/lib/utils';

interface ModuleRoute {
  id: string;
  label: string;
  description: string;
  action: () => void;
  category: 'survey' | 'profile' | 'chat' | 'content' | 'other';
}

/**
 * Admin-only module navigator for quick testing
 * Activated with Cmd/Ctrl+K, only visible to admins
 */
const AdminModuleNavigator = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [recent, setRecent] = useState<string[]>(() => 
    JSON.parse(localStorage.getItem('adminModuleRecent') || '[]')
  );
  
  // Fetch user modules
  const { data: userModules, error: modulesError } = useUserModules();
  
  // Check if currently in override mode
  const { data: currentModule, error: currentModuleError } = useCurrentUserModule();
  const isInOverrideMode = isModuleOverride(currentModule);
  
  // Log errors for debugging
  if (modulesError) {
    console.error('Error fetching user modules:', modulesError);
  }
  if (currentModuleError) {
    console.error('Error fetching current module:', currentModuleError);
  }
  
  // Fetch survey questions for survey modules
  const pulseSurveyQuery = useQuery({
    queryKey: ['survey-questions', 1],
    queryFn: async () => {
      const { data } = await apiClient.get('/api/survey/1/questions');
      return data;
    },
    enabled: !!userModules?.some(m => m.module.screenType === 'survey' && m.module.surveyId === 1)
  });
  
  const shapeSurveyQuery = useQuery({
    queryKey: ['survey-questions', 2],
    queryFn: async () => {
      const { data } = await apiClient.get('/api/survey/2/questions');
      return data;
    },
    enabled: !!userModules?.some(m => m.module.screenType === 'survey' && m.module.surveyId === 2)
  });
  
  // Module override mutation
  const overrideMutation = useMutation({
    mutationFn: async (config: {
      moduleId: number;  // This uniquely identifies the module
      progressCheckpoint: number;
    }) => {
      const { data } = await apiClient.post('/api/admin/module-override', config);
      return data;
    },
    onSuccess: async () => {
      // Invalidate and refetch the current user module
      await queryClient.invalidateQueries({ queryKey: ['current-user-module'] });
      // Also invalidate user modules list to ensure consistency
      await queryClient.invalidateQueries({ queryKey: ['user-modules'] });
      
      // Close the modal first
      setOpen(false);
      
      // Small delay to ensure queries have updated before navigation
      setTimeout(() => {
        navigate('/module');
      }, 50);
    },
    onError: (error) => {
      console.error('Failed to override module:', error);
      // Show error but keep modal open so user can try again
      alert('Failed to override module. Please try again.');
    }
  });
  
  // Exit override mutation
  const exitOverrideMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/api/admin/exit-override', {});
      return data;
    },
    onSuccess: async () => {
      // Clear any localStorage flags
      localStorage.removeItem('hasActiveOverride');
      localStorage.removeItem('overrideModuleId');
      
      // Invalidate queries to refresh the current module
      await queryClient.invalidateQueries({ queryKey: ['current-user-module'] });
      await queryClient.invalidateQueries({ queryKey: ['user-modules'] });
      
      // Close the modal
      setOpen(false);
    },
    onError: (error) => {
      console.error('Failed to exit override:', error);
      alert('Failed to exit override mode. Please try again.');
    }
  });
  
  // Generate routes dynamically from user modules
  const generateRoutes = (): ModuleRoute[] => {
    if (!userModules) return [];
    
    const routes: ModuleRoute[] = [];
    
    
    // Add Updates route (static)
    routes.push({
      id: 'updates',
      label: 'Updates Page',
      description: 'Main dashboard',
      category: 'other',
      action: () => navigate('/updates')
    });
    
    // Process each user module
    userModules.forEach((userModule) => {
      const module = userModule.module;
      
      // Debug log for survey intros
      if (module.screenType === 'survey_intro') {
        console.log('Survey intro module:', {
          name: module.name,
          id: module.id,
          surveyId: module.surveyId,
          data: module.data
        });
      }
      
      // Determine category
      const getCategory = (screenType: string) => {
        switch (screenType) {
          case 'survey':
          case 'survey_intro':
            return 'survey';
          case 'profile':
            return 'profile';
          case 'chat':
            return 'chat';
          default:
            return 'other';
        }
      };
      
      if (module.screenType === 'survey' && module.surveyId) {
        // Special handling for survey modules - create start/middle/end routes
        const surveyName = module.surveyId === 1 ? 'PULSE' : 'SHAPE';
        const questions = module.surveyId === 1 ? pulseSurveyQuery.data : shapeSurveyQuery.data;
        const totalQuestions = questions?.length || 20; // Default to 20 if not loaded
        
        routes.push({
          id: `${module.id}-start`,
          label: `${surveyName} Survey - Start`,
          description: `Begin ${surveyName} assessment (Question 1)`,
          category: 'survey',
          action: () => overrideMutation.mutate({
            moduleId: module.id,
            progressCheckpoint: 0
          })
        });
        
        const midPoint = Math.floor(totalQuestions / 2);
        routes.push({
          id: `${module.id}-mid`,
          label: `${surveyName} Survey - Middle`,
          description: `${surveyName} assessment Question ${midPoint + 1}`,
          category: 'survey',
          action: () => overrideMutation.mutate({
            moduleId: module.id,
            progressCheckpoint: midPoint
          })
        });
        
        routes.push({
          id: `${module.id}-end`,
          label: `${surveyName} Survey - End`,
          description: `${surveyName} assessment Question ${totalQuestions} (last)`,
          category: 'survey',
          action: () => overrideMutation.mutate({
            moduleId: module.id,
            progressCheckpoint: totalQuestions - 1
          })
        });
      } else {
        // Standard route for other module types
        routes.push({
          id: module.id.toString(),
          label: module.name,
          description: module.uiDescription || `${module.screenType} module`,
          category: getCategory(module.screenType),
          action: () => {
            console.log(`Overriding to module: ${module.name}`, {
              moduleId: module.id
            });
            
            overrideMutation.mutate({
              moduleId: module.id,
              progressCheckpoint: 0
            });
          }
        });
      }
    });
    
    return routes;
  };
  
  const routes = generateRoutes();
  
  // Filter routes based on search
  const filteredRoutes = routes.filter(route => 
    route.label.toLowerCase().includes(search.toLowerCase()) ||
    route.description.toLowerCase().includes(search.toLowerCase()) ||
    route.category.toLowerCase().includes(search.toLowerCase())
  );
  
  // Group by category
  const groupedRoutes = filteredRoutes.reduce((acc, route) => {
    if (!acc[route.category]) acc[route.category] = [];
    acc[route.category].push(route);
    return acc;
  }, {} as Record<string, ModuleRoute[]>);
  
  // Add to recent when selected
  const handleSelect = (route: ModuleRoute) => {
    const newRecent = [route.id, ...recent.filter(id => id !== route.id)].slice(0, 5);
    setRecent(newRecent);
    localStorage.setItem('adminModuleRecent', JSON.stringify(newRecent));
    route.action();
  };
  
  // Get recent routes
  const recentRoutes = recent
    .map(id => routes.find(r => r.id === id))
    .filter(Boolean) as ModuleRoute[];
  
  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(!open);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);
  
  // Only available for admins - check after all hooks
  if (!user?.isAdmin) return null;
  
  // Show loading indicator while fetching modules
  const isLoading = !userModules;
  
  if (!open) {
    return (
      <>
        <div className="fixed bottom-4 right-4 flex items-center gap-2 z-50">
          {/* Exit override button */}
          {isInOverrideMode && (
            <button
              onClick={() => exitOverrideMutation.mutate()}
              className="bg-orange-100 hover:bg-orange-200 text-orange-700 border border-orange-300 px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm transition-colors"
              disabled={exitOverrideMutation.isPending}
            >
              <TestTube2 className="w-4 h-4" />
              <span>{exitOverrideMutation.isPending ? 'Exiting...' : 'Exit Override'}</span>
            </button>
          )}
          
          {/* Command button */}
          <button
            onClick={() => setOpen(true)}
            className="p-3 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors"
            title="Module Navigator (⌘K)"
          >
            <Command className="w-5 h-5" />
          </button>
        </div>
      </>
    );
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50" 
        onClick={() => setOpen(false)} 
      />
      
      {/* Command palette */}
      <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-2xl">
        {/* Header */}
        <div className="border-b p-4">
          <div className="flex items-center gap-3">
            <Search className="w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search modules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 shadow-none focus-visible:ring-0 text-lg"
              autoFocus
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="max-h-96 overflow-auto">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-flex items-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                <span>Loading modules...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Recent */}
              {recentRoutes.length > 0 && search === '' && (
                <div className="p-2">
                  <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Recent
                  </div>
                  {recentRoutes.map(route => (
                    <button
                      key={`recent-${route.id}`}
                      onClick={() => handleSelect(route)}
                      className={cn(
                        "w-full text-left p-3 rounded-md transition-colors",
                        route.id === 'exit-override'
                          ? "bg-orange-50 hover:bg-orange-100 border border-orange-200"
                          : "hover:bg-gray-100"
                      )}
                      disabled={overrideMutation.isPending || exitOverrideMutation.isPending}
                    >
                      <div className={cn(
                        "font-medium",
                        route.id === 'exit-override' && "text-orange-700"
                      )}>
                        {route.label}
                      </div>
                      <div className={cn(
                        "text-sm",
                        route.id === 'exit-override' ? "text-orange-600" : "text-gray-500"
                      )}>
                        {route.description}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Grouped routes */}
              {Object.entries(groupedRoutes).map(([category, items]) => (
                <div key={category} className="p-2">
                  <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {category}
                  </div>
                  {items.map(route => (
                    <button
                      key={route.id}
                      onClick={() => handleSelect(route)}
                      className={cn(
                        "w-full text-left p-3 rounded-md transition-colors",
                        route.id === 'exit-override'
                          ? "bg-orange-50 hover:bg-orange-100 border border-orange-200"
                          : "hover:bg-gray-100"
                      )}
                      disabled={overrideMutation.isPending || exitOverrideMutation.isPending}
                    >
                      <div className={cn(
                        "font-medium",
                        route.id === 'exit-override' && "text-orange-700"
                      )}>
                        {route.label}
                      </div>
                      <div className={cn(
                        "text-sm",
                        route.id === 'exit-override' ? "text-orange-600" : "text-gray-500"
                      )}>
                        {route.description}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
              
              {/* No results */}
              {filteredRoutes.length === 0 && !isLoading && (
                <div className="p-8 text-center text-gray-500">
                  No modules found for "{search}"
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t p-3 text-xs text-gray-500 flex justify-between">
          <span>Admin Mode • {user.email}</span>
          <span>Press ESC to close</span>
        </div>
        
        {/* Loading overlay */}
        {overrideMutation.isPending && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
              <span className="text-sm">Setting up module...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminModuleNavigator;